import React, { memo, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
import { ConfluenceExperimentsContext } from '@post-office/growth-common/confluence-experiments-context';
import {
	TEMPLATE_XFLOW_EXPERIMENT_KEY,
	usePostOfficeSideNavMessages,
} from '@post-office/placements--growth/confluence-side-nav/contexts/post-office-side-nav-message-provider';
// import { TestPlacement } from '@post-office/placements--growth/confluence-side-nav/test';
import { Placement as ConfluenceSideNav } from '@post-office/placements/confluence-side-nav/frontend';

import {
	PlacementFetchProvider,
	usePlacementPreloadedData,
} from '@atlassian/confluence-post-office-provider/entry-points/preload-placement';

import { useOpenAgentsAwarenessModal } from '@confluence/agents-awareness-modal/entry-points/useOpenAgentsAwarenessModal.ts';
import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils.ts';
import { useCreateSpaceStoreActions } from '@confluence/create-space/entry-points/createSpace.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { fg } from '@confluence/feature-gating';
import { type WithFlagsProps, withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useRouteActions, useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import { HOME } from '@confluence/named-routes/entry-points/routes.ts';
import { useUniversalCreateActions } from '@confluence/create-blank-fabric-page/entry-points/useUniversalCreate.ts';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

const PO_CONFLUENCE_SIDE_NAV = 'poConfluenceSideNavCreate';

type SideNavClickHandler = (nextBestActionType: NextBestAction, messages: unknown[]) => void;

type InternalProps = {
	flags?: WithFlagsProps['flags'];
};

const PlacementInternal = memo(({ flags }: InternalProps): React.JSX.Element => {
	const [preloadedInitialData, setInitialData] = usePlacementPreloadedData('confluence-side-nav');
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const { push } = useRouteActions();
	const { openDrawer } = useCreateSpaceStoreActions();
	const openAgentsAwarenessModal = useOpenAgentsAwarenessModal();
	const sideNavRef = useRef(null);
	const currentRoute = useRouteUrl({
		selector: (routeUrl: string | undefined) => routeUrl || '',
	});
	const { setIsOpen: setIsUniversalCreateOpen } = useUniversalCreateActions();
	const { templateXflow } = useContext(ConfluenceExperimentsContext);
	const sideNavMessages = usePostOfficeSideNavMessages();
	const enableSideNavMessage = sideNavMessages?.enablePostOfficeSideNavMessage;
	const disableSideNavMessage = sideNavMessages?.disablePostOfficeSideNavMessage;
	const isTemplateXflowEligible =
		templateXflow?.currentPageTemplateData.isEligible === true &&
		templateXflow.reminderState !== 'dismissed' &&
		!templateXflow.isTerminallyDismissed &&
		fg('twcg-2112-twc-xflow-conf-pages-templates-gate');

	useEffect(() => {
		if (!enableSideNavMessage || !disableSideNavMessage) {
			return;
		}

		if (isTemplateXflowEligible) {
			enableSideNavMessage(TEMPLATE_XFLOW_EXPERIMENT_KEY);
		} else {
			disableSideNavMessage(TEMPLATE_XFLOW_EXPERIMENT_KEY);
		}

		return () => {
			disableSideNavMessage(TEMPLATE_XFLOW_EXPERIMENT_KEY);
		};
	}, [disableSideNavMessage, enableSideNavMessage, isTemplateXflowEligible]);

	const onDismiss = useCallback(
		(data: unknown) => {
			setInitialData(data);
		},
		[setInitialData],
	);

	const onSideNavClick = useCallback<SideNavClickHandler>(
		(nextBestActionType: NextBestAction, data: unknown) => {
			setInitialData(data);

			switch (nextBestActionType) {
				case 'CREATE_PAGE': {
					push(
						buildCreationLink({
							contentType: 'page',
							source: PO_CONFLUENCE_SIDE_NAV,
						}),
					);
					break;
				}
				case 'CREATE_WHITEBOARD': {
					push(
						buildCreationLink({
							contentType: 'whiteboard',
							source: PO_CONFLUENCE_SIDE_NAV,
						}),
					);
					break;
				}
				case 'CREATE_SPACE': {
					// The second argument for openDrawer is the location string, which can be undefined or 'topCreateNav'
					// Since we're using the side nav to open the drawer, undefined is passed here.
					openDrawer(sideNavRef, undefined);
					break;
				}
				case 'CREATE_WITH_ROVO_NUDGE': {
					void setIsUniversalCreateOpen(true);
					sessionStorage.setItem(keys.AIFC_CREATE_SPOTLIGHT_SESSION_KEY, 'true');
					break;
				}
				case 'MCP_SETUP_NUDGE': {
					openAgentsAwarenessModal({ initialStep: 'mcp-setup' });
					break;
				}
				case 'RESURRECTED_USER_NUDGE': {
					push(HOME.toUrl(undefined, { query: { feedType: 'POPULAR' } }));
					break;
				}
				default:
					return;
			}
		},
		[openAgentsAwarenessModal, openDrawer, push, setInitialData, setIsUniversalCreateOpen],
	);

	const memoizedPlacementProps = useMemo(
		() => ({
			preloadedInitialData,
			onDismiss,
			onSideNavClick,
			currentRoute,
			...(flags && { flags }),
			disableInitialData: Boolean(preloadedInitialData),
		}),
		[preloadedInitialData, onDismiss, onSideNavClick, currentRoute, flags],
	);

	/**
	 * This is memoized to avoid any fetch calls getting cancelled inside `ConfluenceSideNav` due to
	 * rerenders from changing context values (.ie usePostOfficeSideNavMessages).
	 */
	const PlacementInternals = useMemo(
		() => (
			<PlacementFetchProvider>
				{/* @ts-expect-error: Props are not type checked */}
				<ConfluenceSideNav {...memoizedPlacementProps} />
			</PlacementFetchProvider>
		),
		[memoizedPlacementProps],
	);

	return (
		<div
			data-testid="confluence-side-nav-placement"
			data-vc="confluence-side-nav-placement"
			{...ssrPlaceholderIdProp}
		>
			{PlacementInternals}
		</div>
	);
});

const PostOfficeConfluenceSideNavPlacementWithFlags = withFlags(
	({ flags }: WithFlagsProps): React.JSX.Element => <PlacementInternal flags={flags} />,
);

export const PostOfficeConfluenceSideNavPlacement: React.MemoExoticComponent<
	() => React.JSX.Element
> = memo((): React.JSX.Element => {
	const isMeetingSignalExperimentEnabled =
		fg('twcg-2202-loom-meeting-nudge-ht-gate') || fg('twcg-2202-loom-meeting-nudge-lt-gate');

	return isMeetingSignalExperimentEnabled ? (
		<PostOfficeConfluenceSideNavPlacementWithFlags />
	) : (
		<PlacementInternal />
	);
});
